import React from 'react';
import moment from 'moment-jalaali';
import { parse, ParsedQuery } from 'query-string';
import { RouteComponentProps } from '@reach/router';
import { MapDispatchToProps, connect, MapStateToProps } from 'react-redux';
import { removeDuplicates } from 'utils/happyFunctions';
import { RootState } from 'store/store';
import protectedRoute from 'components/protectedRoute';
import PrintSingleTask from './PrintSingleTask';
import {
  requireProviderDocuments,
  requireProviderDocumentsFn,
} from 'store/providers/providerSlice';
import { ITaskPrintTemplateObject } from 'store/entity/IPrintTemplatePart';
import {
  PrintSingleTaskProvider,
  IPrintSingleTaskContext,
} from 'store/context/PrintSingleTaskContext';
import {
  setTotalProgress,
  setAllDataFetched,
  updateProgressCount,
  IProgress,
} from 'store/progress/ProgressSlice';
import messages from './Messages';
import ProgressBar from 'components/ProgressBar';
import { convertDigitToProperFormat } from 'utils/number';
import { appDispatch } from 'store/store';
import {
  ICompany,
  ICompanyTemplate,
  IProject,
} from 'store/companies/companiesTypes';
import { IAssignmentView, ITask } from 'store/tasks/tasksType';
import {
  getSingleCompany,
  getSingleCompanyFn,
} from 'store/companies/companiesSlice';
import {
  getChildrenTasks,
  getChildrenTasksFn,
  getSingleTask,
  getSingleTaskFn,
} from 'store/tasks/tasksSlice';
import { fetchAllUsers, fetchAllUsersFn } from 'store/users/usersSlice';
import { notifyError } from 'components/Notify/Notify';

type ParamProps = {
  taskId: string;
  companyId: string;
};
type StateToProps = {
  parsed: ParsedQuery;
  progress: IProgress;
};
type DispatchToProps = {
  getCompany: (
    companyId: number,
    projectStub: boolean
  ) => ReturnType<typeof getSingleCompanyFn>;
  getSubTasks: (
    params: GetSubTasksParams
  ) => ReturnType<typeof getChildrenTasksFn>;
  getDocuments: (
    documentIds: number[]
  ) => ReturnType<typeof requireProviderDocumentsFn>;
  requireUsers: (userIds: number[]) => ReturnType<typeof fetchAllUsersFn>;
  requireSingleTask: (taskId: number) => ReturnType<typeof getSingleTaskFn>;
  setTotalProgress: (total: number) => void;
  setAllDataFetched: () => void;
  updateProgressCount: () => void;
};
type OwnProps = RouteComponentProps<ParamProps>;
type ComponentProps = OwnProps & StateToProps & DispatchToProps;
type ComponentStates = IPrintSingleTaskContext & {
  error?: any;
  printTemplate?: ITaskPrintTemplateObject;
};

type GetSubTasksParams = {
  parentId: number;
  projectId: number;
  companyId: number;
};

class PrintSingleTaskContainer extends React.PureComponent<
  ComponentProps,
  ComponentStates
> {
  state: ComponentStates = {
    company: undefined,
    members: undefined,
    taskIds: undefined,
    allTasks: undefined,
    printTemplate: undefined,
    relatedProviderItems: undefined,
  };

  migratePrintTemplate = (
    template: ITaskPrintTemplateObject
  ): ITaskPrintTemplateObject => {
    const migratePart = (part: any) => {
      if (part.part === 'verifier') {
        part.part = 'verifiers';
      }
      return part;
    };

    return {
      header: template.header?.map(migratePart) || [],
      body:
        template.body?.map((item) => {
          const migrated = migratePart({ ...item });
          if (migrated.part === 'subTasks' && migrated.options?.tableBody) {
            migrated.options.tableBody =
              migrated.options.tableBody.map(migratePart);
          }
          if (migrated.part === 'subTasks' && migrated.options?.tableHeader) {
            migrated.options.tableHeader =
              migrated.options.tableHeader.map(migratePart);
          }
          return migrated;
        }) || [],
    };
  };

  componentDidMount() {
    this.fetchDataForPrintTask(Number(this.props.taskId));
  }

  fetchDataForPrintTask = async (selfTaskId: number) => {
    const {
      setTotalProgress: startProgress,
      updateProgressCount: updateFetchCount,
      setAllDataFetched: allDataFetched,
      companyId,
      getCompany,
      getSubTasks,
      getDocuments,
      requireUsers,
      requireSingleTask,
    } = this.props;

    try {
      startProgress(6);
      const selfTask = await requireSingleTask(selfTaskId)
        .then((res) => res.task)
        .catch(() => null);
      if (!selfTask) {
        this.setErrorMessage('selfTask');
        return;
      }
      const relatedProviderItemIds = [];
      if (selfTask?.relatedProviderItemId) {
        relatedProviderItemIds.push(selfTask.relatedProviderItemId);
      }
      // let printTemplate = selfTask.printTemplate;
      if (!selfTask.printTemplate) {
        this.setErrorMessage('printTemplate not exists!');
        return;
      }
      updateFetchCount();

      // projectStub is false to get projectMembers
      const company = await getCompany(Number(companyId), false)
        .then((res) => res.company)
        .catch(() => null);
      if (!company) {
        this.setErrorMessage('company');
        return;
      }

      updateFetchCount();

      const params: GetSubTasksParams = {
        parentId: selfTask.id,
        projectId: selfTask.projectId,
        companyId: Number(companyId),
      };
      const subTasks = await getSubTasks(params)
        .then((res) => res.data)
        .catch(() => null);
      if (!subTasks) {
        this.setErrorMessage('subTasks');
        return;
      }
      updateFetchCount();

      const subTasksRelatedItems = this.getRelatedProviderItemIds(subTasks);
      const relatedProviderItems = (
        await getDocuments([
          ...relatedProviderItemIds,
          ...subTasksRelatedItems,
        ]).catch(() => null)
      )?.items;
      if (!relatedProviderItems) {
        this.setErrorMessage('relatedProviderItems');
        return;
      }
      updateFetchCount();
      let parentTask: ITask | null = null;
      if (selfTask.parentId) {
        parentTask = await requireSingleTask(selfTask.parentId)
          .then((res) => res.task)
          .catch(() => null);
        if (!parentTask) {
          this.setErrorMessage('parentTask');
          return;
        }
      }
      updateFetchCount();

      const tasks = [selfTask, ...subTasks];
      if (parentTask) tasks.push(parentTask);
      const memberIds = this.getTaskMembers(tasks, company.members!);
      const members = (await requireUsers(memberIds).catch(() => null))?.users;
      if (!members) {
        this.setErrorMessage('members');
        return;
      }
      updateFetchCount();
      let printTemplate: ITaskPrintTemplateObject;
      const template = company.templates?.find(
        (it: ICompanyTemplate) => it.taskId === selfTask.templateId
      );
      try {
        const parsedTemplate = JSON.parse(template!.task!.printTemplate!);
        printTemplate = this.migratePrintTemplate(parsedTemplate);
      } catch {
        this.setErrorMessage('Bad printTemplate keys');
        return;
      }
      const ancestorId: number | undefined = selfTask.ancestors?.[1];
      let ancestorTask: ITask | null;
      if (ancestorId) {
        ancestorTask = await requireSingleTask(ancestorId as number)
          .then((res) => {
            return res.task;
          })
          .catch(() => null);
        tasks.push(ancestorTask);
        updateFetchCount();
      }
      this.setState(
        {
          allTasks: tasks,
          members,
          company: company,
          taskIds: {
            selfTask: selfTask.id,
            parentTask: parentTask?.id,
            ancestorTask: ancestorId,
          },
          printTemplate,
          relatedProviderItems,
        },
        () => {
          const subTasksTitle = parentTask?.subTasksTitle;
          const selfProjectName = company.projects?.find(
            (it: IProject) => it.id === selfTask.projectId
          )?.name;
          const date = convertDigitToProperFormat(
            moment().format('jD jMMMM')
          ) as string;
          window.document.title = messages.getPrintTaskTitle(
            selfProjectName || '',
            date,
            subTasksTitle
          );
          allDataFetched();
        }
      );
    } catch (e) {
      // console.log(e);
      notifyError(e);
      this.setState({ error: 'error' });
    }
  };

  setErrorMessage = (message: any) => {
    this.setState({ error: message });
  };

  getTaskMembers = (
    tasks: ITask[],
    companyMembers: ICompany['members']
  ): number[] => {
    const memberIds = tasks.reduce((list: number[], task: ITask) => {
      if (Object.keys(task.assignee).length > 0) {
        list.push(...Object.values(task.assignee).map((a) => a.userId));
      }
      if (Object.keys(task.followers).length > 0) {
        list.push(...Object.values(task.followers).map((f) => f.userId));
      }
      Object.values(task.verifiers || {}).forEach(
        (verifier: IAssignmentView) => {
          list.push(verifier.userId);
        }
      );
      if (task.taskPublisher) {
        list.push(task.taskPublisher.userId);
      }
      if (task.subTaskPublisher) {
        list.push(task.subTaskPublisher.userId);
      }
      return list;
    }, []);
    memberIds.push(...Object.values(companyMembers).map((m) => m.userId));
    return removeDuplicates(memberIds);
  };

  getRelatedProviderItemIds = (subTasks: ITask[]) => {
    const itemIds = subTasks.reduce((list: number[], task: ITask) => {
      if (task.relatedProviderItemId?.id) {
        list.push(task.relatedProviderItemId.id);
      }
      return list;
    }, []);
    return removeDuplicates(itemIds);
  };

  render() {
    const { progress } = this.props;
    const {
      error,
      taskIds,
      company,
      members,
      allTasks,
      printTemplate,
      relatedProviderItems,
    } = this.state;
    console.log(printTemplate);
    return !error && printTemplate && progress.allDataFetched ? (
      <PrintSingleTaskProvider
        value={{
          company,
          members,
          taskIds,
          allTasks,
          relatedProviderItems,
        }}
      >
        <PrintSingleTask printTemplate={printTemplate} />
      </PrintSingleTaskProvider>
    ) : (
      <div>
        <ProgressBar count={progress.count} total={progress.total} />
        {error}
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<
  StateToProps,
  OwnProps & RouteComponentProps,
  RootState
> = (state, props) => {
  let parsed: ParsedQuery = {};
  if (props.location) {
    parsed = parse(props.location.search);
  }
  const progress = state.progress;
  return { progress, parsed };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  OwnProps
> = () => {
  return {
    getCompany: (companyId: number, projectStub: boolean) =>
      appDispatch(getSingleCompany({ companyId, projectStub })),
    getSubTasks: (params: GetSubTasksParams) =>
      appDispatch(
        getChildrenTasks({
          parentId: params.parentId,
          projectId: params.projectId,
          companyId: params.companyId,
        })
      ),
    getDocuments: (itemIds: number[]) =>
      appDispatch(requireProviderDocuments({ documentIds: itemIds })),
    requireUsers: (userList: number[]) =>
      appDispatch(fetchAllUsers({ usersIds: userList })),
    requireSingleTask: (taskId: number) =>
      appDispatch(getSingleTask({ taskId })),
    setTotalProgress: (total: number) => appDispatch(setTotalProgress(total)),
    setAllDataFetched: () => appDispatch(setAllDataFetched()),
    updateProgressCount: () => appDispatch(updateProgressCount()),
  };
};

export default protectedRoute(
  connect(mapStateToProps, mapDispatchToProps)(PrintSingleTaskContainer)
);
